interface DragScrollOptions {
  itemSelector?: string;
}

/**
 * Enables drag-to-scroll behavior on a horizontally scrollable element.
 * Only activates on hover-capable devices.
 *
 * Adds `data-dragging` attribute while dragging for styling (e.g., `cursor: grabbing`).
 */
export function enableDragScroll(
  element: HTMLElement,
  signal: AbortSignal,
  options: DragScrollOptions = {},
): void {
  const { itemSelector = ":scope > * > *" } = options;

  if (!window.matchMedia("(hover: hover)").matches) return;

  let isDragging = false;
  let startX = 0;
  let lastX = 0;
  let deltaX = 0;
  let scrollSnapTimeout = 0;
  let pointerCaptureTarget: Element | null = null;

  const handleMouseDown = (event: MouseEvent) => {
    if (!(event.target instanceof Element)) return;
    if (event.metaKey || event.button !== 0) return;
    if (document.documentElement.hasAttribute("data-scroll-locked")) return;
    if (event.target.closest("a, button")) return;

    event.preventDefault();
    clearTimeout(scrollSnapTimeout);

    isDragging = true;
    startX = event.clientX;
    lastX = event.clientX;

    element.style.scrollSnapType = "none";
    element.dataset.dragging = "";
  };

  const handleMouseMove = (event: MouseEvent) => {
    if (!isDragging) return;

    deltaX = event.clientX - lastX;
    lastX = event.clientX;

    element.scrollLeft -= deltaX;
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    isDragging = false;
    delete element.dataset.dragging;

    const children = element.querySelectorAll(itemSelector);
    const closestElement = Array.from(children).find((child) => {
      const rect = child.getBoundingClientRect();
      if (deltaX > 0) {
        return rect.left + 10 > -rect.width;
      }
      return rect.left + 10 > 0;
    });

    if (!closestElement) return;
    if (!(closestElement instanceof HTMLElement)) return;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    element.scrollTo({
      left: closestElement.offsetLeft,
      behavior: prefersReducedMotion ? "auto" : "smooth",
    });

    scrollSnapTimeout = window.setTimeout(() => {
      element.style.removeProperty("scroll-snap-type");
    }, 2000);
  };

  const handlePointerDown = (event: PointerEvent) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest("a, button")) return;
    event.target.setPointerCapture(event.pointerId);
    pointerCaptureTarget = event.target;
  };

  const handlePointerUp = (event: PointerEvent) => {
    pointerCaptureTarget?.releasePointerCapture(event.pointerId);
    pointerCaptureTarget = null;
  };

  const handleClick = (event: MouseEvent) => {
    if (!(event.target instanceof Element)) return;
    if (Math.abs(startX - lastX) > 10) {
      event.preventDefault();
      event.stopPropagation();
      startX = lastX;
    }
  };

  element.addEventListener("mousedown", handleMouseDown, { signal });
  element.addEventListener("mousemove", handleMouseMove, { signal });
  element.addEventListener("mouseup", handleMouseUp, { signal });
  element.addEventListener("pointerdown", handlePointerDown, { signal });
  element.addEventListener("pointerup", handlePointerUp, { signal });
  element.addEventListener("click", handleClick, { signal, capture: true });
}
